'use client';
import type { CSSProperties, ReactNode, Ref } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Ccontainer } from '../Ccontainer';
import { ColorType } from '@/theme/colors';
import { ContainerPreset } from '@/constants/componentPresets';
import { pxToRem } from '@/theme/spacing';
const ERROR_VALUE = "error";
const FOCUSED_VALUE = "focused";
const RESTING_VALUE = "resting";
const KEYBOARD_OPEN_RESIZE_GRACE_MS = 500;
const SHELL_DEFAULT_RADIUS_PX = 24;
const SHELL_DEFAULT_PADDING_BLOCK_PX = 8;
const SHELL_DEFAULT_PADDING_INLINE_PX = 15;
const INPUT_SHADOWS = {
    resting: 'rgb(102 254 193 / 17%) 0px 1px 0px 0px inset, rgb(255 255 255 / 5%) 0px 0px 0px 1px inset, rgba(0, 0, 0, 0.6) 1px 2px 5px 0px inset, rgb(102 254 193 / 15%) 0px -1px 0px 0px inset',
    hover: 'rgba(102, 254, 193, 0.22) 0px 1px 0px 0px inset, rgba(255, 255, 255, 0.04) 0px 0px 0px 1px, rgba(102, 254, 193, 0.13) 0px -4px 8px 0px inset, rgba(0, 0, 0, 0.1) 1px 2px 5px 0px inset, rgba(102, 254, 193, 0.25) 0px -1px 0px 0px inset',
    focused: 'rgb(102 254 193 / 22%) 0px 1px 0px 0px inset, rgb(255 255 255 / 4%) 0px 0px 0px 1px, rgb(102 254 193 / 13%) 0px -4px 8px 0px inset, rgba(0, 0, 0, 0.1) 1px 2px 5px 0px inset, rgb(102 254 193 / 25%) 0px -1px 0px 0px inset, rgb(102 254 193 / 7%) 0px 1px 4px 0px',
    error: 'rgb(224 49 49 / 7%) 0px 3px 2px 1px, rgb(255 255 255 / 5%) 0px 0px 0px 1px, rgb(255 255 255 / 7%) 0px -1px 1px 0px inset, rgb(224 49 49 / 27%) 0px -4px 8px 0px inset, rgba(0, 0, 0, 0.1) 1px 2px 5px 0px inset',
} as const;
interface ChromeContainerProps {
    backgroundColor: ColorType;
    backgroundOpacity: number;
    backdropBlur: number;
    borderColor: ColorType;
    borderOpacity: number;
    glow: number;
    boxShadow: string;
}
interface InputShellContainerOverrides {
    backgroundColor?: ColorType;
    backgroundOpacity?: number;
    borderColor?: ColorType;
    borderOpacity?: number;
    borderWidth?: number;
    innerBorder?: boolean;
    boxShadow?: string;
    height?: number;
}
function buildChromeContainerProps(input: {
    disabled: boolean;
    isError: boolean;
    isFocused: boolean;
    isHovered: boolean;
}): ChromeContainerProps {
    const { disabled, isError, isFocused, isHovered } = input;
    const baseProps = {
        backgroundColor: ColorType.Black,
        backgroundOpacity: 0.2,
        backdropBlur: 0,
    };
    if (disabled) {
        return {
            ...baseProps,
            backgroundOpacity: 0.1,
            borderColor: ColorType.White,
            borderOpacity: 0.06,
            glow: 0,
            boxShadow: INPUT_SHADOWS.resting,
        };
    }
    if (isError) {
        return {
            ...baseProps,
            borderColor: ColorType.Danger,
            borderOpacity: 0.44,
            glow: 0,
            boxShadow: INPUT_SHADOWS.error,
        };
    }
    if (isFocused) {
        return {
            ...baseProps,
            borderColor: ColorType.Accent,
            borderOpacity: 0.3,
            glow: 1,
            boxShadow: INPUT_SHADOWS.focused,
        };
    }
    if (isHovered) {
        return {
            ...baseProps,
            borderColor: ColorType.Accent,
            borderOpacity: 0.2,
            glow: 0,
            boxShadow: INPUT_SHADOWS.hover,
        };
    }
    return {
        ...baseProps,
        borderColor: ColorType.Accent,
        borderOpacity: 0.15,
        glow: 0,
        boxShadow: INPUT_SHADOWS.resting,
    };
}
function scrollActiveElementIntoView(): void {
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement) {
        activeElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
}
function useKeyboardOpenScrollSync(isFocused: boolean): void {
    useEffect(() => {
        if (!isFocused)
            return;
        const visualViewport = window.visualViewport;
        if (!visualViewport)
            return;
        let cleaned = false;
        const cleanupViewportResize = (): void => {
            cleaned = true;
            visualViewport.removeEventListener('resize', handleViewportResize);
        };
        const handleViewportResize = (): void => {
            if (cleaned)
                return;
            cleanupViewportResize();
            scrollActiveElementIntoView();
        };
        visualViewport.addEventListener('resize', handleViewportResize, { once: true });
        const timeoutId = setTimeout(() => {
            if (cleaned)
                return;
            cleanupViewportResize();
        }, KEYBOARD_OPEN_RESIZE_GRACE_MS);
        return () => {
            cleanupViewportResize();
            clearTimeout(timeoutId);
        };
    }, [isFocused]);
}
function getInputShellContainerOverrides(input: InputShellContainerOverrides): InputShellContainerOverrides {
    const overrides: InputShellContainerOverrides = {};
    if (input.backgroundColor != null) {
        overrides.backgroundColor = input.backgroundColor;
    }
    if (input.backgroundOpacity != null) {
        overrides.backgroundOpacity = input.backgroundOpacity;
    }
    if (input.borderColor != null) {
        overrides.borderColor = input.borderColor;
    }
    if (input.borderOpacity != null) {
        overrides.borderOpacity = input.borderOpacity;
    }
    if (input.borderWidth != null) {
        overrides.borderWidth = input.borderWidth;
    }
    if (input.innerBorder != null) {
        overrides.innerBorder = input.innerBorder;
    }
    if (input.boxShadow != null) {
        overrides.boxShadow = input.boxShadow;
    }
    if (input.height != null) {
        overrides.height = input.height;
    }
    return overrides;
}
export interface CinputshellProps {
    children: ReactNode;
    isFocused: boolean;
    isError?: boolean;
    disabled?: boolean;
    radius?: number;
    padding?: string;
    backgroundColor?: ColorType;
    backgroundOpacity?: number;
    borderColor?: ColorType;
    borderOpacity?: number;
    borderWidth?: number;
    innerBorder?: boolean;
    boxShadow?: string;
    height?: number;
    justifyContent?: 'flex-start' | 'space-between' | 'center';
    onClick?: () => void;
    className?: string;
    style?: CSSProperties;
    containerStyle?: CSSProperties;
    rootRef?: Ref<HTMLDivElement>;
}
export function Cinputshell({ children, isFocused, isError = false, disabled = false, radius, padding, backgroundColor, backgroundOpacity, borderColor, borderOpacity, borderWidth, innerBorder, boxShadow, height, justifyContent = 'flex-start', onClick, className, style, containerStyle, rootRef, }: CinputshellProps): React.JSX.Element {
    const [isHovered, setIsHovered] = useState(false);
    const chromeProps = useMemo(() => buildChromeContainerProps({ disabled, isError, isFocused, isHovered }), [disabled, isError, isFocused, isHovered]);
    useKeyboardOpenScrollSync(isFocused);
    const defaultPadding = `${pxToRem(SHELL_DEFAULT_PADDING_BLOCK_PX)} ${pxToRem(SHELL_DEFAULT_PADDING_INLINE_PX)}`;
    const containerOverrides = getInputShellContainerOverrides({
        backgroundColor,
        backgroundOpacity,
        borderColor,
        borderOpacity,
        borderWidth,
        innerBorder,
        boxShadow,
        height,
    });
    return (
      <div ref={rootRef} className={className} style={{ position: 'relative', width: '100%', ...style }} onMouseEnter={() => !disabled && setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
        <Ccontainer preset={ContainerPreset.TextInput} {...chromeProps} {...containerOverrides} padding={padding ?? defaultPadding} radius={radius ?? SHELL_DEFAULT_RADIUS_PX} onClick={onClick} display="flex" alignItems="center" justifyContent={justifyContent} smooth style={{ cursor: disabled ? 'not-allowed' : 'text', ...containerStyle }}>
          {children}
        </Ccontainer>
      </div>
    );
}
